import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Users, Target, Wallet, Clock, Search, Filter, ArrowLeft, Inbox, ShieldCheck, Download, LogOut, GripVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { getStats, listLeads, getPipeline, listConsents, exportConsents, updateStage } from "@/lib/api";
import { toast } from "sonner";
import { TierBadge } from "@/components/TierBadge";
import OutcomeDialog from "@/components/OutcomeDialog";
import LeadDetail from "@/components/LeadDetail";
import SettingsPanel from "@/components/SettingsPanel";
import UsersPanel from "@/components/UsersPanel";
import RemindersPanel from "@/components/RemindersPanel";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import TodayPanel from "@/components/TodayPanel";
import CriticitaPanel from "@/components/CriticitaPanel";
import { LOGO } from "@/lib/brand";
import { useAuth } from "@/lib/auth";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const ESITO_BADGE = {
  qualificato: "bg-emerald-100 text-emerald-800 border-emerald-300",
  consolidamento: "bg-blue-100 text-blue-800 border-blue-300",
  "ad-agio": "bg-amber-100 text-amber-800 border-amber-300",
  ko: "bg-rose-100 text-rose-800 border-rose-300",
};
const ESITO_LABEL = { qualificato: "Qualificato", consolidamento: "Consolidamento", "ad-agio": "Incubatore", ko: "Non Fattibile" };

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [board, setBoard] = useState(null);
  const [stages, setStages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [esitoFilter, setEsitoFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [consents, setConsents] = useState([]);
  const [dragOver, setDragOver] = useState(null);
  const [outcome, setOutcome] = useState(null);

  const moveLead = async (id, from, stage, esitoFinale) => {
    setBoard((b) => {
      const card = (b?.[from] || []).find((l) => l.id === id);
      if (!card) return b;
      return { ...b, [from]: b[from].filter((l) => l.id !== id), [stage]: [{ ...card, pipeline_stage: stage }, ...(b[stage] || [])] };
    });
    try {
      await updateStage(id, stage, esitoFinale);
      toast.success(`Spostato in "${stage}"`);
    } catch {
      toast.error("Spostamento non riuscito");
    }
    refresh();
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/lead-id");
    const from = e.dataTransfer.getData("text/lead-stage");
    if (!id || from === stage) return;
    if (stage === "Erogato" || stage === "Perso") {
      const card = (board?.[from] || []).find((l) => l.id === id);
      setOutcome({ id, from, stage, name: card?.contatto?.nome || "lead" });
      return;
    }
    await moveLead(id, from, stage);
  };

  const refresh = useCallback(async () => {
    const [s, l, p, c] = await Promise.all([
      getStats(),
      listLeads(esitoFilter === "all" ? {} : { esito: esitoFilter }),
      getPipeline(),
      listConsents(),
    ]);
    setStats(s); setLeads(l); setBoard(p.board); setStages(p.stages); setConsents(c);
  }, [esitoFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = leads.filter((l) => {
    if (!search) return true;
    const c = l.contatto || {};
    return `${c.nome} ${c.email} ${c.telefono}`.toLowerCase().includes(search.toLowerCase());
  });

  const downloadConsents = async () => {
    const blob = await exportConsents();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "registro_consensi_gdpr.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background dark">
      <div className="bg-slate-950 min-h-screen text-slate-100">
        {/* Header */}
        <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/80 border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO} alt="Leadders" className="h-7 sm:h-8 w-auto brightness-0 invert" />
              <span className="hidden md:inline text-slate-400 text-sm border-l border-white/20 pl-3 whitespace-nowrap">CRM Consulente</span>
            </div>
            <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
              <span className="hidden lg:inline text-sm text-slate-400 mr-1">{user?.name || user?.email}</span>
              <ChangePasswordDialog />
              <Button data-testid="back-home-link" asChild variant="ghost" size="sm" className="gap-2 text-slate-300 hover:text-white px-2 sm:px-3">
                <Link to="/"><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Landing</span></Link>
              </Button>
              <Button data-testid="logout-button" onClick={signOut} variant="ghost" size="sm"
                className="gap-2 text-slate-300 hover:text-white px-2 sm:px-3">
                <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Esci</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8">
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Kpi testid="kpi-total" icon={Users} label="Lead totali" value={stats?.total ?? "—"} color="#60A5FA" />
            <Kpi testid="kpi-rate" icon={Target} label="Tasso qualifica" value={stats ? `${stats.tasso_qualifica}%` : "—"} color="#34D399" />
            <Kpi testid="kpi-value" icon={Wallet} label="Valore potenziale" value={stats ? fmt(stats.valore_potenziale) : "—"} color="#FBBF24" />
            <Kpi testid="kpi-qualified" icon={Clock} label="Qualificati" value={stats?.qualificati ?? "—"} color="#A78BFA" />
          </div>

          <Tabs defaultValue="today">
            <div className="-mx-3 px-3 sm:mx-0 sm:px-0 overflow-x-auto no-scrollbar">
              <TabsList data-testid="dashboard-tabs" className="bg-slate-900 border border-white/10 w-max min-w-full sm:min-w-0 sm:w-auto justify-start h-10">
                <TabsTrigger data-testid="tab-today" value="today">Oggi</TabsTrigger>
                <TabsTrigger data-testid="tab-pipeline" value="pipeline">Pipeline</TabsTrigger>
                <TabsTrigger data-testid="tab-criticita" value="criticita" className="gap-1.5">Criticità{stats?.criticita_aperte > 0 && <span data-testid="tab-criticita-count" className="text-[10px] font-bold px-1.5 rounded-full bg-amber-400 text-slate-900">{stats.criticita_aperte}</span>}</TabsTrigger>
                <TabsTrigger data-testid="tab-leads" value="leads">Lead</TabsTrigger>
                <TabsTrigger data-testid="tab-consents" value="consents">GDPR</TabsTrigger>
                <TabsTrigger data-testid="tab-reminders" value="reminders">Richiami</TabsTrigger>
                <TabsTrigger data-testid="tab-settings" value="settings">Impostazioni</TabsTrigger>
                {user?.role === "admin" && <TabsTrigger data-testid="tab-users" value="users">Utenti</TabsTrigger>}
              </TabsList>
            </div>

            <TabsContent value="today" className="mt-6"><TodayPanel onOpenLead={setSelected} /></TabsContent>
            <TabsContent value="criticita" className="mt-6"><CriticitaPanel onOpenLead={setSelected} onChanged={refresh} /></TabsContent>
            <TabsContent value="reminders" className="mt-6"><RemindersPanel isAdmin={user?.role === "admin"} /></TabsContent>
            <TabsContent value="settings" className="mt-6"><SettingsPanel isAdmin={user?.role === "admin"} /></TabsContent>
            {user?.role === "admin" && <TabsContent value="users" className="mt-6"><UsersPanel /></TabsContent>}

            {/* Pipeline Kanban */}
            <TabsContent value="pipeline" className="mt-6">
              <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 -mx-3 px-3 sm:mx-0 sm:px-0 snap-x snap-mandatory sm:snap-none">
                {stages.map((stage, i) => (
                  <div key={stage} data-testid={`pipeline-stage-${i + 1}`}
                    onDragOver={(e) => { e.preventDefault(); if (dragOver !== stage) setDragOver(stage); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => onDrop(e, stage)}
                    className={`shrink-0 w-[85vw] max-w-[320px] sm:w-72 snap-center bg-slate-900 rounded-xl border transition-colors ${dragOver === stage ? "border-accent ring-2 ring-accent/40" : "border-white/10"}`}>
                    <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                      <span className="font-display font-semibold text-sm text-white">{stage}</span>
                      <Badge variant="secondary" className="bg-slate-800 text-slate-300">{board?.[stage]?.length || 0}</Badge>
                    </div>
                    <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto min-h-[80px]">
                      {(board?.[stage] || []).map((l) => (
                        <button key={l.id} data-testid={`pipeline-card-${l.id}`} onClick={() => setSelected(l.id)}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("text/lead-id", l.id); e.dataTransfer.setData("text/lead-stage", stage); e.dataTransfer.effectAllowed = "move"; }}
                          className="w-full text-left p-3 rounded-lg bg-slate-800/60 hover:bg-slate-800 border border-white/5 transition-colors cursor-grab active:cursor-grabbing">
                          <div className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 min-w-0"><GripVertical className="h-3.5 w-3.5 text-slate-600 shrink-0" /><span className="font-medium text-sm text-white truncate">{l.contatto?.nome || "Senza nome"}</span></span>
                            <TierBadge tier={l.tier} testid={`pipeline-tier-${l.id}`} className="shrink-0" />
                            {l.source === "acchiappo" && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 shrink-0" data-testid={`pipeline-source-${l.id}`}>Acchiappo</span>}
                            <span className="font-mono text-xs text-accent font-bold shrink-0">{l.score_categoriale}/10</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1 truncate">{l.score_categoriale_label}</div>
                          <div className="mt-2">
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border ${ESITO_BADGE[l.esito_algoritmo]}`}>
                              {ESITO_LABEL[l.esito_algoritmo]}
                            </span>
                          </div>
                        </button>
                      ))}
                      {(!board?.[stage] || board[stage].length === 0) && (
                        <div className="text-center py-6 text-slate-600 text-xs">Vuoto</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Lead list */}
            <TabsContent value="leads" className="mt-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input data-testid="lead-search" placeholder="Cerca per nome, email, telefono..."
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-slate-900 border-white/10 text-white" />
                </div>
                <Select value={esitoFilter} onValueChange={setEsitoFilter}>
                  <SelectTrigger data-testid="lead-filter-esito" className="w-full sm:w-56 bg-slate-900 border-white/10 text-white">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli esiti</SelectItem>
                    <SelectItem value="qualificato">Qualificato</SelectItem>
                    <SelectItem value="consolidamento">Consolidamento</SelectItem>
                    <SelectItem value="ad-agio">Incubatore Ad Agio</SelectItem>
                    <SelectItem value="ko">Non Fattibile KO</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-xl border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-slate-400">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">Contatto</th>
                      <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Categoria</th>
                      <th className="text-center font-medium px-4 py-3">Cat.</th>
                      <th className="text-center font-medium px-4 py-3 hidden sm:table-cell">Assic.</th>
                      <th className="text-left font-medium px-4 py-3">Esito</th>
                      <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Stage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l) => (
                      <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSelected(l.id)}
                        className="border-t border-white/5 hover:bg-slate-900/60 cursor-pointer">
                        <td className="px-4 py-3">
                          <div className="font-medium text-white flex items-center gap-2">{l.contatto?.nome || "—"}<TierBadge tier={l.tier} testid={`list-tier-${l.id}`} /></div>
                          <div className="text-xs text-slate-500">{l.contatto?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{l.score_categoriale_label}</td>
                        <td className="px-4 py-3 text-center font-mono font-bold text-accent">{l.score_categoriale}/10</td>
                        <td className="px-4 py-3 text-center font-mono text-slate-300 hidden sm:table-cell">{l.score_assicurativo}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${ESITO_BADGE[l.esito_algoritmo]}`}>
                            {ESITO_LABEL[l.esito_algoritmo]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs hidden lg:table-cell">{l.pipeline_stage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div className="text-center py-16 text-slate-500">
                    <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    Nessun lead. Completa l'intervista dalla landing per generarne uno.
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Consensi GDPR */}
            <TabsContent value="consents" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-slate-300">
                  <ShieldCheck className="h-5 w-5 text-emerald-400" />
                  <span className="text-sm">Registro consensi tracciato e immutabile — {consents.length} record</span>
                </div>
                <Button data-testid="export-consents-button" onClick={downloadConsents} size="sm"
                  className="gap-2 bg-accent hover:bg-accent/90 text-white">
                  <Download className="h-4 w-4" /> Esporta CSV
                </Button>
              </div>
              <div className="rounded-xl border border-white/10 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-slate-400">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">Interessato</th>
                      <th className="text-left font-medium px-4 py-3 hidden sm:table-cell">Data/ora</th>
                      <th className="text-center font-medium px-4 py-3">Consenso</th>
                      <th className="text-center font-medium px-4 py-3 hidden md:table-cell">Versione</th>
                      <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consents.map((c) => (
                      <tr key={c.id} data-testid={`consent-row-${c.id}`} className="border-t border-white/5">
                        <td className="px-4 py-3">
                          <div className="font-medium text-white">{c.nome || "—"}</div>
                          <div className="text-xs text-slate-500">{c.email}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-400 font-mono text-xs hidden sm:table-cell">
                          {new Date(c.timestamp).toLocaleString("it-IT")}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded border bg-emerald-100 text-emerald-800 border-emerald-300">
                            Acconsente
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-400 hidden md:table-cell">v{c.versione}</td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs hidden lg:table-cell">{c.ip || "n.d."}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {consents.length === 0 && (
                  <div className="text-center py-16 text-slate-500">
                    <ShieldCheck className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    Nessun consenso registrato. Verrà tracciato automaticamente al termine di ogni intervista.
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </main>
        {selected && (
          <LeadDetail leadId={selected} onClose={() => setSelected(null)}
            onUpdated={() => { refresh(); }} stages={stages} />
        )}
        {outcome && (
          <OutcomeDialog stage={outcome.stage} leadName={outcome.name} onCancel={() => setOutcome(null)}
            onConfirm={async (ef) => { await moveLead(outcome.id, outcome.from, outcome.stage, ef); setOutcome(null); }} />
        )}
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-slate-900 border border-white/10 p-3.5 sm:p-5">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400 leading-tight">{label}</span>
      <Icon className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" style={{ color }} />
    </div>
    <div className="font-mono font-bold text-xl sm:text-3xl text-white mt-1.5 sm:mt-2 truncate">{value}</div>
  </div>
);
